/**
 * Reports on donor-written text other people can read (GH-958; App Review guideline 1.2).
 *
 * A reader of the comment wall, the Q&A list or the "Just now" gift list taps Report on one item,
 * picks a reason and optionally adds a short note. No account is needed. The answer is the same
 * whether the report was new or a repeat, and never says how many others reported it.
 *
 * What a report names:
 *  - a comment or question: the submission's id, which the wall payload carries;
 *  - a "Just now" entry (its donor-chosen name and any approved note): the opaque `reportRef` the
 *    row carries. It is a salted one-way hash of the gift's id, never the id — the public feed has
 *    no ids.
 *
 * Staff see the reported text, the reasons and any notes, and decide Keep or Remove. Nothing here
 * identifies a reporter to anyone.
 */
import { z } from "zod";
export const REPORT_REASONS = ["offensive", "harassment", "spam", "misleading", "other"];
export const reportReasonSchema = z.enum(REPORT_REASONS);
/** The longest optional note a reporter may add (code points). Mirrors the column CHECK. */
export const REPORT_NOTE_MAX_LENGTH = 300;
// Control and bidirectional-override characters, as for a donor's own note.
// eslint-disable-next-line no-control-regex -- matching control characters is the point.
const NOTE_FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F‪-‮⁦-⁩]/u;
const reportNoteSchema = z
    .string()
    .transform((value) => value.trim())
    .refine((value) => [...value].length <= REPORT_NOTE_MAX_LENGTH, {
    message: `A note can be at most ${REPORT_NOTE_MAX_LENGTH} characters.`,
})
    .refine((value) => !NOTE_FORBIDDEN.test(value), { message: "A note must be plain text." })
    .transform((value) => (value === "" ? null : value));
export const reportTargetSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.enum(["comment", "question"]),
        submissionId: z.string().uuid(),
    }),
    z.object({
        kind: z.literal("gift"),
        ref: z.string().min(1).max(512),
    }),
]);
export const contentReportRequestSchema = z
    .object({
    target: reportTargetSchema,
    reason: reportReasonSchema,
    note: reportNoteSchema.nullish().transform((value) => value ?? null),
    /**
     * A random id the app or browser made for itself and keeps. It lets two people on one network
     * count as two reporters and one person repeating themselves count once. It identifies no one:
     * it is hashed with a server secret before storage.
     */
    deviceId: z
        .string()
        .regex(/^[A-Za-z0-9_-]{8,64}$/)
        .optional(),
})
    .strict();
/** The whole answer. Identical for a new report and a repeat. */
export const contentReportAckSchema = z.object({ received: z.literal(true) });
/* ------------------------------------------------------------------- staff */
export const reportedItemSchema = z.object({
    kind: z.enum(["comment", "question", "gift"]),
    /** The item's id: the submission, or the donation or pledge of a "Just now" entry. */
    targetId: z.string().uuid(),
    /** The text as readers saw it. Plain text (`dir="auto"`). */
    body: z.string(),
    reportCount: z.number().int().positive(),
    /** Readers cannot see it now: enough different people reported it. A host decides. */
    withheld: z.boolean(),
    reasons: z.object({
        offensive: z.number().int().nonnegative(),
        harassment: z.number().int().nonnegative(),
        spam: z.number().int().nonnegative(),
        misleading: z.number().int().nonnegative(),
        other: z.number().int().nonnegative(),
    }),
    /** What reporters wrote, oldest first. Never who wrote it. */
    notes: z.array(z.string()),
    firstReportedAt: z.string(),
});
export const reportedItemsResponseSchema = z.object({ items: z.array(reportedItemSchema) });
export const reportDecisionRequestSchema = z
    .object({ decision: z.enum(["keep", "remove"]) })
    .strict();
//# sourceMappingURL=content-reports.js.map